import type {
  LocatedOsmElement,
  OsmElement,
  OsmElementWithTags,
  OsmProfile,
  OsmTags,
} from "../../lib/osm.ts";
import { osmProfile } from "../../lib/osm.ts";
import {
  containingBoundaryValue,
  hasTagValue,
  tagValue,
} from "../../lib/osm/tags.ts";
import type { OsmState } from "../MapScreen/selection/osmLookup.ts";

export interface SelectionPanelViewModel {
  title: string;
  subtitle: string;
  details: OsmProfile | undefined;
  osmHref: string | undefined;
  tagEntries: Array<[string, string]>;
  nearbyPlaces: LocatedOsmElement[];
  contextFeatures: LocatedOsmElement[];
}

const NEARBY_PLACE_LIMIT = 6;
const CONTEXT_FEATURE_LIMIT = 8;
const NEARBY_PLACE_IDENTITY_KEYS = [
  "amenity",
  "shop",
  "tourism",
  "leisure",
  "historic",
  "office",
  "craft",
  "healthcare",
] as const;

export function selectionPanelViewModel(
  osm: OsmState,
  osmBaseUrl: string,
): SelectionPanelViewModel {
  const candidates = osm.status === "success"
    ? [osm.best, ...osm.alternatives]
    : [];
  const displayElement = osm.status === "success" ? osm.active : undefined;
  const details = displayElement ? osmProfile(displayElement.tags) : undefined;
  const alternatives = candidates.filter((element) =>
    displayElement ? !isSameOsmElement(element, displayElement) : true
  );
  const { nearbyPlaces, contextFeatures } = partitionAlternatives(alternatives);

  return {
    title: details?.name ?? "Selected place",
    subtitle: details ? details.typeLabel ?? "Place" : "",
    details,
    osmHref: displayElement
      ? `${osmBaseUrl}/${displayElement.type}/${displayElement.id}`
      : undefined,
    tagEntries: nonEmptyTagEntries(displayElement?.tags),
    nearbyPlaces,
    contextFeatures,
  };
}

function nonEmptyTagEntries(
  tags: OsmTags | undefined,
): Array<[string, string]> {
  return Object.entries(tags ?? {}).filter(([, value]) => hasTagValue(value));
}

function isSameOsmElement(
  left: OsmElement,
  right: OsmElement,
): boolean {
  return left.type === right.type && left.id === right.id;
}

function partitionAlternatives(
  alternatives: LocatedOsmElement[],
): Pick<SelectionPanelViewModel, "nearbyPlaces" | "contextFeatures"> {
  const nearbyPlaces: LocatedOsmElement[] = [];
  const contextFeatures: LocatedOsmElement[] = [];
  const nearbyKeys = new Set<string>();
  const contextKeys = new Set<string>();

  for (const element of alternatives) {
    if (isNearbyPlaceAlternative(element)) {
      const key = semanticAlternativeKey(element);
      if (!nearbyKeys.has(key) && nearbyPlaces.length < NEARBY_PLACE_LIMIT) {
        nearbyKeys.add(key);
        nearbyPlaces.push(element);
      }
      continue;
    }

    const key = semanticAlternativeKey(element);
    if (
      !contextKeys.has(key) && contextFeatures.length < CONTEXT_FEATURE_LIMIT
    ) {
      contextKeys.add(key);
      contextFeatures.push(element);
    }
  }

  return { nearbyPlaces, contextFeatures };
}

function isNearbyPlaceAlternative(element: OsmElementWithTags): boolean {
  const details = osmProfile(element.tags);
  if (!details.name) return false;
  if (isBroadContextFeature(element.tags)) return false;

  return NEARBY_PLACE_IDENTITY_KEYS.some((key) =>
    tagValue(element.tags, key).length > 0
  );
}

function isBroadContextFeature(tags: OsmTags): boolean {
  return Boolean(
    containingBoundaryValue(tags) ||
      tagValue(tags, "route") ||
      tagValue(tags, "place") ||
      tagValue(tags, "highway") ||
      tagValue(tags, "waterway") ||
      tagValue(tags, "landuse") ||
      tagValue(tags, "natural") ||
      (tagValue(tags, "building") && !hasNearbyPlaceIdentity(tags)),
  );
}

function hasNearbyPlaceIdentity(tags: OsmTags): boolean {
  return NEARBY_PLACE_IDENTITY_KEYS.some((key) => tagValue(tags, key));
}

function semanticAlternativeKey(element: OsmElementWithTags): string {
  const details = osmProfile(element.tags);
  const normalizedName = details.name?.trim().toLocaleLowerCase();
  const normalizedType = details.typeLabel?.trim().toLocaleLowerCase();

  if (normalizedName && normalizedType) {
    return `${normalizedName}|${normalizedType}`;
  }
  if (!normalizedName && normalizedType) return `unnamed|${normalizedType}`;
  return `${element.type}:${element.id}`;
}
